import { Component, DestroyRef, ElementRef, Input, computed, effect, inject, signal, viewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { EditorialIconComponent } from '../../../../shared/components/editorial-icon/editorial-icon.component';
import {
  Affinity, Award, CharacterTag, EditorialContent, EditorialTab, ProductFact, SensoryAxis,
  affinities, awards, characterTags, hasText, productFacts, publishedAxes
} from '../../../../core/models/editorialContent.model';
import { CatalogSpirit } from '../../../../core/models/catalogSpirit.model';
import { CatalogData } from '../../../../core/models/catalogData.model';
import { CatalogTranslationPipe } from '../../../../core/pipes/catalogTranslation';
import { SpiritSensoryProfileComponent } from './spirit-sensory-profile/spirit-sensory-profile.component';
import { SpiritCharacterTagsComponent } from './spirit-character-tags/spirit-character-tags.component';
import { SpiritAffinitiesComponent } from './spirit-affinities/spirit-affinities.component';
import { SpiritProductFactsComponent } from './spirit-product-facts/spirit-product-facts.component';
import { SpiritAwardsComponent } from './spirit-awards/spirit-awards.component';

const components = [
  EditorialIconComponent,
  SpiritSensoryProfileComponent,
  SpiritCharacterTagsComponent,
  SpiritAffinitiesComponent,
  SpiritProductFactsComponent,
  SpiritAwardsComponent
]

/**
 * Canonical field order per section, mirroring `ContentSection::fields()` in the backend. The
 * backend sends only the fields the resolved PROFILE allows, so these lists decide the order
 * of what arrives, never which fields exist.
 */
const TASTING_FIELDS = ['appearance', 'nose', 'palate', 'finish'] as const;
const PRODUCTION_FIELDS = [
  'rawMaterial', 'fermentation', 'distillation', 'maturation', 'finishCask', 'botanicals',
  'aromatisation', 'filtration', 'water', 'distillery', 'origin', 'ageClassification'
] as const;

/**
 * Catalogue identity shown inside "Información del producto", in reading order.
 *
 * Only fields the header does not already state: type, category, region and country are printed
 * beside the name, and repeating them here would make the block a second header rather than the
 * spec sheet it is. Nothing is derived or formatted — an absent field is simply not a row.
 */
const CATALOG_ROWS = ['brand', 'age', 'volume', 'capacity'] as const;

/** One catalogue row: its own i18n label and the catalogue entry it names. */
export interface CatalogRow {
  key:   string;
  label: string;
  value: CatalogData;
}

/** One entry of the section index: the anchor it jumps to and the heading it names. */
export interface EditorialSectionLink {
  id:    string;
  label: string;
}

/** Height of the pinned index plus breathing room: the reading band starts right under it. */
const INDEX_OFFSET = 68;

/** Minimum scroll, in px, that each of the trailing short sections gets before the next one takes over. */
const MIN_TAIL_STEP_PX = 40;

/** After the guest drags the index by hand, auto-centring stays away for this long. */
const MANUAL_DRAG_GRACE_MS = 1500;

/** Vertical scroll, in px, after which a hand-drag of the index stops being respected. */
const MANUAL_DRAG_RELEASE_PX = 24;

/** Below this the index would name almost the whole sheet: a short sheet is its own map. */
const MIN_INDEXED_SECTIONS = 3;

/**
 * Accent per visual group, mixed toward the SpiritsRim ember before it reaches the sheet.
 *
 * A gin whose dominant note is botanical must not turn into a green product: the family colour
 * shifts the sheet, the brand colour anchors it. The mix ratio lives in CSS (`color-mix`), so
 * this map only has to name the family hue.
 */
const GROUP_ACCENT: Record<string, string> = {
  botanical: '#5E8A5A', fruit: '#C9862F', spice: '#A8552C', grain: '#A88A4E',
  sweet: '#B98A3C', smoke: '#6E6A66', wood: '#8A5A34', maritime: '#4E7C86',
  floral: '#A05C7A', dairy: '#9A8F7E', vegetal: '#4F8560'
};

/** SpiritsRim ember: the readable tint of the #9b1c00 primary on a dark ground. */
const EMBER = '#D3623C';

/**
 * Editorial block of the public spirit sheet: the eighteen editorial fields plus every derived
 * block the assessment pipeline publishes.
 *
 * Everything is adaptive and, crucially, INDEPENDENT: each block decides on its own data, so a
 * spirit with only a sensory profile still renders one, and a spirit with prose but no
 * assessment shows no empty scales. There is no global `isEmpty()` switch on purpose — with it
 * the granular fallback the sheet needs is impossible, because the presence of any editorial
 * content would decide the fate of every legacy block at once.
 *
 * There is no serving block. Glassware, ice, garnish and measures belong to the venue, so the
 * component has nothing to render them from.
 */
@Component({
  selector: 'app-spirit-editorial',
  standalone: true,
  imports: [TranslateModule, CatalogTranslationPipe, ...components],
  templateUrl: './spirit-editorial.component.html',
  styleUrl: './spirit-editorial.component.scss'
})
export class SpiritEditorialComponent {

  private host = inject(ElementRef<HTMLElement>);
  private destroyRef = inject(DestroyRef);
  private indexNav = viewChild<ElementRef<HTMLElement>>('indexNav');
  private translate = inject(TranslateService);
  private languageChange = toSignal(this.translate.onLangChange);

  private content = signal<EditorialContent | null>(null);
  private catalog = signal<CatalogSpirit | null>(null);

  @Input({ required: true }) set data(value: EditorialContent | null | undefined) {
    this.content.set(value ?? null);
  }

  /**
   * Catalogue side of "Información del producto". Optional on purpose: the block still renders
   * its declared facts for a caller that has no catalogue data to hand.
   */
  @Input() set catalogSpirit(value: CatalogSpirit | null | undefined) {
    this.catalog.set(value ?? null);
  }

  /** Split into paragraphs so a long narrative does not render as one dense block. */
  storytelling = computed<string[]>(() => {
    const narrative = this.content()?.narrative?.narrative;
    if (!hasText(narrative)) return [];

    return narrative!
      .split(/\n+/)
      .map(paragraph => paragraph.trim())
      .filter(paragraph => paragraph.length > 0);
  });

  tastingSteps = computed<EditorialTab[]>(() =>
    this.buildEntries(this.content()?.tasting, TASTING_FIELDS, 'editorial.tasting_')
  );

  productionEntries = computed<EditorialTab[]>(() =>
    this.buildEntries(this.content()?.production, PRODUCTION_FIELDS, 'editorial.production_')
  );

  ingredients = computed<string | null>(() => {
    const ingredients = this.content()?.ingredients?.ingredients;

    return hasText(ingredients) ? ingredients!.trim() : null;
  });

  sensory = computed<SensoryAxis[]>(() => publishedAxes(this.content()));

  tags = computed<CharacterTag[]>(() => characterTags(this.content()));

  affinities = computed<Affinity[]>(() => affinities(this.content()));

  facts = computed<ProductFact[]>(() => productFacts(this.content()));

  awards = computed<Award[]>(() => awards(this.content()));

  /** Catalogue rows that exist. A missing field is not a row with an empty value. */
  catalogRows = computed<CatalogRow[]>(() => {
    const catalog = this.catalog();
    if (!catalog) return [];

    return CATALOG_ROWS.flatMap(key => {
      const value = catalog[key];

      return value ? [{ key, label: 'spirit.' + key, value }] : [];
    });
  });

  /**
   * Catalogue identity and declared facts share one section but never one list: an editorial
   * fact is a tri-state claim we verified, a catalogue row is a datum the venue already had.
   */
  hasProductInformation = computed<boolean>(() =>
    this.catalogRows().length > 0 || this.facts().length > 0
  );

  /**
   * Index of the sheet, built from the sections that actually rendered, so a link can never
   * point at an anchor that is not there. Each entry reuses its own section heading rather than
   * a second, shorter vocabulary: two names for one section is how an index starts lying.
   */
  sectionLinks = computed<EditorialSectionLink[]>(() => {
    this.languageChange();

    const sections: [boolean, string, string][] = [
      [this.tags().length > 0,             'character',   'editorial.character'],
      [this.sensory().length > 0,          'sensory',     'editorial.sensory'],
      [this.tastingSteps().length > 0,     'tasting',     'editorial.tasting_note'],
      [this.productionEntries().length > 0, 'production', 'editorial.production'],
      [!!this.ingredients(),               'ingredients', 'editorial.ingredients'],
      [this.affinities().length > 0,       'affinities',  'editorial.affinities'],
      [this.hasProductInformation(),       'facts',       'editorial.facts'],
      [this.awards().length > 0,           'awards',      'editorial.awards']
    ];

    return sections
      .filter(([present]) => present)
      .map(([, id, labelKey]) => ({
        id: this.anchorOf(id),
        label: this.translate.instant(labelKey)
      }));
  });

  /** The index earns its space only on a sheet long enough to get lost in. */
  showSectionIndex = computed<boolean>(() => this.sectionLinks().length >= MIN_INDEXED_SECTIONS);

  anchorOf(section: string): string {
    return 'editorial-' + section;
  }

  /** Section being read, marked in the index (phones only: the index is pinned there). */
  activeId = signal<string | null>(null);
  fadeStart = signal(false);
  fadeEnd = signal(false);

  private scroller: HTMLElement | null = null;
  private frame = 0;
  private spyLocked = false;
  private lockTimer: ReturnType<typeof setTimeout> | undefined;
  private lastManualDrag = 0;
  private manualDragScrollTop = 0;
  private settleTimer: ReturnType<typeof setTimeout> | undefined;
  private onScrollEnd = () => this.releaseLockSoon();
  private onUserScroll = () => this.releaseLock();
  private phone = window.matchMedia('(width < 992px)');
  private onScroll = () => {
    if (!this.frame) this.frame = requestAnimationFrame(() => { this.frame = 0; this.updateActive(); });
  };

  constructor() {
    const restart = () => setTimeout(() => this.setupSpy());
    effect(() => { this.sectionLinks(); restart(); });
    this.phone.addEventListener('change', restart);
    this.destroyRef.onDestroy(() => {
      this.phone.removeEventListener('change', restart);
      this.detachScroller();
      cancelAnimationFrame(this.frame);
      this.clearLockTimers();
    });
  }

  /**
   * Scroll-spy: a passive scroll listener on the sheet's pane that only schedules one
   * requestAnimationFrame per frame; the work itself is a handful of rect reads.
   */
  private setupSpy(): void {
    this.detachScroller();
    this.updateFades();

    const root = (this.host.nativeElement as HTMLElement).closest('main') as HTMLElement | null;
    if (!this.phone.matches || !this.showSectionIndex() || !root) {
      this.activeId.set(null);
      return;
    }

    this.scroller = root;
    root.addEventListener('scroll', this.onScroll, { passive: true });
    // The guest taking over the scroll always ends any lock: programmatic jumps fire neither.
    root.addEventListener('touchstart', this.onUserScroll, { passive: true });
    root.addEventListener('wheel', this.onUserScroll, { passive: true });
    this.updateActive();
  }

  /**
   * The active section is the last one whose heading has passed a fixed line just under the
   * pinned index (scroll position >= its threshold).
   *
   * Short sections at the end of the sheet can never reach that line: the pane runs out of scroll
   * first, so a plain "passed the line" rule would jump from the last-but-N section straight to
   * the last. Their thresholds are therefore spread over the scroll that is left, so they take
   * turns as the guest moves through the last pixels, in both directions.
   */
  private updateActive(): void {
    const root = this.scroller;
    if (!root || this.spyLocked) return;

    const host = this.host.nativeElement as HTMLElement;
    const rootTop = root.getBoundingClientRect().top;
    const max = root.scrollHeight - root.clientHeight;
    const ids = this.sectionLinks().map(link => link.id);

    const thresholds = ids.map(id => {
      const heading = host.querySelector(`#${id}`);
      return heading
        ? heading.getBoundingClientRect().top - rootTop + root.scrollTop - INDEX_OFFSET - 2
        : Infinity;
    });

    const firstUnreachable = thresholds.findIndex(threshold => threshold > max);
    if (firstUnreachable > 0 || (firstUnreachable === 0 && max > 0)) {
      const tail = thresholds.length - firstUnreachable;
      const lastReachable = firstUnreachable - 1;
      // Each tail section gets at least MIN_TAIL_STEP_PX of scroll; if the last reachable section
      // leaves less room than that, it gives up the difference and is reached a little earlier.
      const floor = lastReachable >= 1 ? thresholds[lastReachable - 1] + 1 : 0;
      const start = Math.max(floor, Math.min(thresholds[lastReachable] ?? 0, max - tail * MIN_TAIL_STEP_PX));
      if (lastReachable >= 0) thresholds[lastReachable] = start;
      const step = (max - start) / tail;
      for (let i = 0; i < tail; i++) thresholds[firstUnreachable + i] = start + step * (i + 1);
    }

    let active: string | null = null;
    thresholds.forEach((threshold, i) => { if (root.scrollTop >= threshold - 0.5) active = ids[i]; });
    if (active !== null && active !== this.activeId()) this.activate(active, false);
    if (active === null && this.activeId() !== null) this.activeId.set(null);
  }

  private activate(id: string, forceCenter: boolean): void {
    this.activeId.set(id);
    // A hand-dragged index is left alone only briefly and only while the guest has not scrolled
    // the sheet vertically since; a tap always recentres.
    const root = this.scroller;
    const dragged = Date.now() - this.lastManualDrag < MANUAL_DRAG_GRACE_MS
      && (!root || Math.abs(root.scrollTop - this.manualDragScrollTop) < MANUAL_DRAG_RELEASE_PX);
    if (forceCenter || !dragged) this.centerChip(id);
  }

  private detachScroller(): void {
    const root = this.scroller;
    if (!root) return;
    root.removeEventListener('scroll', this.onScroll);
    root.removeEventListener('touchstart', this.onUserScroll);
    root.removeEventListener('wheel', this.onUserScroll);
    root.removeEventListener('scrollend', this.onScrollEnd);
    this.scroller = null;
  }

  private clearLockTimers(): void {
    clearTimeout(this.lockTimer);
    clearTimeout(this.settleTimer);
  }

  /** Every lock path ends here, so a lock can never outlive its jump. */
  private releaseLock(): void {
    this.clearLockTimers();
    this.scroller?.removeEventListener('scrollend', this.onScrollEnd);
    this.spyLocked = false;
  }

  /** The last scroll events of a jump can land a beat after it ends. */
  private releaseLockSoon(): void {
    clearTimeout(this.settleTimer);
    this.settleTimer = setTimeout(() => this.releaseLock(), 250);
  }

  /** Scrolls the index container itself: scrollIntoView would also move the page vertically. */
  private centerChip(id: string): void {
    const nav = this.indexNav()?.nativeElement;
    const chip = nav?.querySelector<HTMLElement>(`a[href="#${id}"]`);
    if (!nav || !chip) return;

    const navBox = nav.getBoundingClientRect();
    const chipBox = chip.getBoundingClientRect();
    const left = nav.scrollLeft + (chipBox.left - navBox.left) - (navBox.width - chipBox.width) / 2;
    nav.scrollTo({ left: Math.max(0, left), behavior: this.reducedMotion() ? 'auto' : 'smooth' });
  }

  private reducedMotion(): boolean {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  noteManualDrag(): void {
    this.lastManualDrag = Date.now();
    this.manualDragScrollTop = this.scroller?.scrollTop ?? 0;
  }

  updateFades(): void {
    const nav = this.indexNav()?.nativeElement;
    if (!nav || !this.phone.matches) {
      this.fadeStart.set(false);
      this.fadeEnd.set(false);
      return;
    }
    this.fadeStart.set(nav.scrollLeft > 2);
    this.fadeEnd.set(nav.scrollLeft + nav.clientWidth < nav.scrollWidth - 2);
  }

  /**
   * The sheet scrolls inside its own container rather than the document, so the browser's own
   * fragment jump would not reach the heading. The href stays real: it is what makes the index
   * a set of links instead of a row of buttons dressed as one.
   */
  scrollToSection(event: Event, id: string): void {
    const target = (this.host.nativeElement as HTMLElement).querySelector('#' + id);
    if (!target) return;

    event.preventDefault();

    // Mark the tapped chip at once and mute the spy until the programmatic scroll settles, or
    // the chips it crosses on the way would flicker and drag the index around. The lock is
    // released by whichever comes first: scrollend, the guest scrolling, a jump that never
    // started (already in place, e.g. at the end of the page), or a safety timeout.
    const root = this.scroller;
    this.releaseLock();
    this.spyLocked = true;
    this.activate(id, true);
    if (root) {
      const startTop = root.scrollTop;
      root.addEventListener('scrollend', this.onScrollEnd, { once: true });
      this.settleTimer = setTimeout(() => { if (root.scrollTop === startTop) this.releaseLock(); }, 200);
    }
    this.lockTimer = setTimeout(() => this.releaseLock(), 1000);

    target.scrollIntoView({ behavior: this.reducedMotion() ? 'auto' : 'smooth', block: 'start' });
  }

  /**
   * The sheet's accent, taken from the DOMINANT character group. A spirit with no published
   * character keeps the brand ember rather than losing its accent altogether, which is what
   * every partial case (facts-and-awards only, prose only) actually receives.
   */
  accent = computed<string>(() => {
    const dominant = this.tags()[0]?.iconKey;

    return (dominant && GROUP_ACCENT[dominant]) || EMBER;
  });

  /**
   * Generic over the section type so the concrete interfaces keep their exact field names
   * instead of being widened to a string map.
   */
  private buildEntries<T extends object>(
    section: T | undefined,
    fields: readonly (keyof T & string)[],
    labelPrefix: string
  ): EditorialTab[] {
    if (!section) return [];

    return fields.flatMap(field => {
      const text = section[field];

      if (typeof text !== 'string' || text.trim().length === 0) {
        return [];
      }

      return [{ field, label: labelPrefix + field, text }];
    });
  }
}
